import { renderTemplate } from '../../components/utils';
import { Translations } from '../../i18n/i18n';
import { ApiSchedule } from '../../types';
import { ValuePiece } from '../Calendar/Calendar';

const getDateMonthDays = (date: Date): number => new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();

export const DEFAULT_SHIP_IN_HOURS = 72;

export type DaysOrderKey = 0 | 1 | 2 | 3 | 4 | 5 | 6;

const DAYS_ORDER = { 1: 1, 2: 2, 3: 3, 4: 4, 5: 5, 6: 6, 0: 7 };

export const sortDays = (a: keyof typeof DAYS_ORDER, b: keyof typeof DAYS_ORDER): number =>
	DAYS_ORDER[a] - DAYS_ORDER[b];

export const parseWeeklyDays = (weeklyDays: string): [DaysOrderKey] => JSON.parse(weeklyDays);

export const stringifyWeeklyDays = (weeklyDays: number[]): string => JSON.stringify(weeklyDays);

export const formatCalendarDate = (value: Date | [ValuePiece, ValuePiece]) => {
	const date = new Date(value.toString());
	return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
};

export const addHours = (date: Date, hours: number) => {
	date.setTime(date.getTime() + hours * 60 * 60 * 1000);
	return date;
};

const checkNumberEnding = (value: number): number => {
	if ([11, 12].includes(value % 100)) {
		return 0;
	}

	if (value > 20 && [1, 2].includes(value % 10)) {
		return 1;
	}

	return 0;
};

const getRecurrenceValueText = (recurrenceValue: number, t: Translations['subscriptions']): string => {
	if (recurrenceValue < 4) {
		return t.schedule.recurrence[recurrenceValue.toString() as keyof typeof t.schedule.recurrence];
	}

	return `${t.schedule.each} ${recurrenceValue}${t.schedule.counterPunctuation[checkNumberEnding(recurrenceValue)]}`;
};

const translateDayOrder = (date: number, t: Translations['subscriptions']): string => {
	if (date < 4) {
		return Object.values(t.schedule.counters).find((c) => c.id === date)?.text || '';
	}

	return `${date}${t.schedule.counterPunctuation[checkNumberEnding(date)]}`;
};

const translateWeekday = (weekday: number, t: Translations['subscriptions']): string =>
	Object.values(t.schedule.weekdays).find((day) => day.id === weekday)?.text || '';

const translateWeeklyDays = (weeklyDays: DaysOrderKey[], t: Translations['subscriptions'], locale: string): string =>
	new Intl.ListFormat(locale, { style: 'long', type: 'conjunction' }).format(
		weeklyDays.sort(sortDays).map((weekDay) => translateWeekday(weekDay, t) + t.schedule.pluralSuffix)
	);

export const translateCounter = (counter: number, t: Translations['subscriptions']): string =>
	Object.values(t.schedule.counters).find((c) => c.id === counter)?.text || '';

export const findNextValidDay = () => {
	let searchDate = new Date();
	let iterations = 0;
	const iterationsLimit = 1000;
	const twoDaysAhead = new Date(new Date().setHours(new Date().getHours() + DEFAULT_SHIP_IN_HOURS));

	while (iterations < iterationsLimit) {
		if (!isWeekend(searchDate) && !isSwedishHoliday(searchDate) && twoDaysAhead < searchDate) {
			return searchDate;
		}
		searchDate.setDate(searchDate.getDate() + 1);
	}

	return new Date();
};

export const getScheduleSuggestions = (startDate: string, listId: number): ApiSchedule[] => {
	const weekDay = new Date(startDate).getDay();
	const date = new Date(startDate).getDate();

	return [
		{
			id: 5,
			productListId: listId,
			recurrencePattern: 'daily',
			recurrenceValue: 2,
			weeklyDays: '[]',
			startDate,
			orders: [],
		},
		{
			id: 1,
			productListId: listId,
			recurrencePattern: 'weekly',
			recurrenceValue: 1,
			weeklyDays: `[${weekDay}]`,
			startDate,
			orders: [],
		},
		{
			id: 3,
			productListId: listId,
			recurrencePattern: 'weekly',
			recurrenceValue: 2,
			weeklyDays: `[${weekDay}]`,
			startDate,
			orders: [],
		},
		{
			id: 2,
			productListId: listId,
			recurrencePattern: 'monthly',
			recurrenceValue: 1,
			monthlyWeekday: weekDay,
			monthlyWeekdayCounter: getProgressionPos(new Date(startDate)),
			monthlyInterval: 'weekday',
			weeklyDays: '[]',
			startDate,
			orders: [],
		},
		{
			id: 4,
			productListId: listId,
			recurrencePattern: 'monthly',
			recurrenceValue: 2,
			monthlyDay: date,
			monthlyInterval: 'date',
			weeklyDays: '[]',
			startDate,
			orders: [],
		},
	];
};

export const getProgressionPos = (date: Date): number => {
	const maxDays = getDateMonthDays(date);
	let day = date.getDate();

	if (day > maxDays - 7) {
		return -1;
	}
	let pos = 1;

	while (day > 7) {
		pos++;
		day = day - 7;
	}

	return pos;
};

export const formatScheduleText = (schedule: ApiSchedule, locale: string, t: Translations['subscriptions']): string => {
	if (!schedule || !schedule.startDate || !schedule.recurrenceValue || !schedule.recurrencePattern) return '';

	const {
		recurrenceValue,
		recurrencePattern,
		monthlyWeekdayCounter,
		monthlyWeekday,
		weeklyDays,
		monthlyDay,
		monthlyInterval,
	} = schedule;

	const recurrenceValueText = getRecurrenceValueText(recurrenceValue, t);
	const parsedWeeklyDays = parseWeeklyDays(weeklyDays);

	if (recurrencePattern === 'daily') {
		return renderTemplate(t.schedule.daily, {
			recurrenceValue: recurrenceValueText,
		});
	} else if (recurrencePattern === 'weekly' && Boolean(parsedWeeklyDays.length)) {
		const weekdayLong = new Date(schedule.startDate).toLocaleDateString(locale, { weekday: 'long' });

		return renderTemplate(t.schedule.weekly, {
			recurrenceValue: recurrenceValueText,
			weekday: parsedWeeklyDays ? translateWeeklyDays(parsedWeeklyDays, t, locale) : weekdayLong,
		});
	} else if (schedule.recurrencePattern === 'monthly') {
		if (monthlyInterval === 'weekday' && monthlyWeekdayCounter && monthlyWeekday !== undefined) {
			return renderTemplate(t.schedule.monthlyWithWeekday, {
				recurrenceValue: recurrenceValueText,
				weekdayCounter: translateCounter(monthlyWeekdayCounter, t),
				weekday: translateWeekday(monthlyWeekday, t),
				suffix: t.schedule.definiteFormSuffix,
			});
		}

		if (monthlyInterval === 'date' && monthlyDay) {
			return renderTemplate(t.schedule.monthly, {
				recurrenceValue: recurrenceValueText,
				date: translateDayOrder(monthlyDay, t),
			});
		}
	}

	return '';
};

const WEEKENDS = [0, 6];

export const isSwedishHoliday = (date: Date): boolean => {
	const day = date.getDate();
	const month = date.getMonth();

	// Define Swedish holidays with their respective dates and months (months are 0-based indexes)
	const SWEDISH_HOLIDAYS = [
		[1, 0], // 1st January, Nyårsdagen (New Year's Day)
		[6, 0], // 6th January, Trettondag jul (Epiphany)
		[28, 2], // 28th March, Skärtorsdag (Maundy Thursday)
		[29, 2], // 29th March, Långfredag (Good Friday)
		[31, 2], // 31st March, Påskdagen (Easter Sunday)
		[1, 3], // 1st April, Annandag påsk (Easter Monday)
		[1, 4], // 1st May, Första maj (May Day)
		[9, 4], // 9th May, Kristi himmelsfärds dag (Ascension Day)
		[19, 4], // 19th May, Pingstdagen (Pentecost)
		[6, 5], // 6th June, Sveriges nationaldag (Swedish National Day)
		[22, 5], // 22nd June, Midsommardagen (Midsummer Day)
		[2, 10], // 2nd November, Alla helgons dag (All Saints' Day)
		[24, 11], // 24th December, Julafton (Christmas Eve)
		[25, 11], // 25th December, Juldagen (Christmas Day)
		[26, 11], // 26th December, Annandag jul (Boxing Day)
	];

	// Check if the given date matches any Swedish holiday date
	return SWEDISH_HOLIDAYS.some((holiday) => holiday[0] === day && holiday[1] === month);
};

export const isWeekend = (date: Date): boolean => WEEKENDS.includes(date.getDay());

const calculateNextOrderDaily = (startDate: string, recurrenceValue: number): Date => {
	const start = new Date(startDate);
	const today = new Date();

	start.setHours(12, 0, 0, 0);
	today.setHours(12, 0, 0, 0);

	const searchDate = new Date(start.valueOf());

	let iterationLimit = 1000; // Prevent infinite loops
	let iterations = 0;

	while (today.getTime() > searchDate.getTime() && iterations < iterationLimit) {
		searchDate.setDate(searchDate.getDate() + recurrenceValue);

		iterations++;
	}

	return searchDate;
};

const calculateNextOrderWeekly = (startDate: string, weeklyDays: number[], recurrenceValue: number): Date => {
	const start = new Date(startDate);
	const today = new Date();

	start.setHours(12, 0, 0, 0);
	today.setHours(12, 0, 0, 0);

	const firstDate = today.getTime() > start.getTime() ? today : start;

	const searchDate = new Date(start.getTime());
	const endDate = new Date(new Date(firstDate.getTime()).setDate(1 + 7 * recurrenceValue + firstDate.getDate()));
	let found = false;

	let iterationLimit = 1000; // Prevent infinite loops
	let iterations = 0;

	while (endDate.getTime() >= searchDate.getTime() && iterations < iterationLimit) {
		if (weeklyDays.includes(searchDate.getDay())) {
			if (today.getTime() <= searchDate.getTime() && start.getTime() <= searchDate.getTime()) break;

			found = true;
		}

		if (searchDate.getDay() === 0 && found) {
			searchDate.setDate(1 + 7 * (recurrenceValue - 1) + searchDate.getDate());
		} else {
			searchDate.setDate(searchDate.getDate() + 1);
		}

		iterations++;
	}

	return new Date(searchDate);
};

const calculateNextOrderMonthly = (schedule: ApiSchedule): Date | null => {
	const { recurrenceValue, monthlyWeekdayCounter, monthlyWeekday, monthlyDay, startDate, monthlyInterval } = schedule;

	if (monthlyInterval === 'weekday' && monthlyWeekdayCounter && monthlyWeekday !== undefined) {
		return calculateNextOrderMonthlyWeekday(startDate, monthlyWeekday, monthlyWeekdayCounter, recurrenceValue);
	}

	if (monthlyInterval === 'date' && monthlyDay) {
		return calculateNextOrderMonthlyDate(startDate, monthlyDay, recurrenceValue);
	}

	return null;
};

const calculateNextOrderMonthlyWeekday = (
	startDate: string,
	weekday: number,
	counter: number,
	recurrenceValue: number
): Date => {
	const start = new Date(startDate);
	const today = new Date();

	start.setHours(12, 0, 0, 0);
	today.setHours(12, 0, 0, 0);

	let firstDate = today.getTime() > start.getTime() ? today : start;
	let endDate = new Date(new Date(firstDate.getTime()).setMonth(firstDate.getMonth() + recurrenceValue));

	// Set searchDate to the startDate
	const searchDate = new Date(start.getTime());

	let iterationLimit = 1000; // Prevent infinite loops
	let iterations = 0;

	// If searchDate is before today, keep adding months by recurrenceValue until searchDate is after today and searchDate's
	// day is equal to weekday from parameters
	while (endDate.getTime() >= searchDate.getTime() && iterations < iterationLimit) {
		const maxDays = getDateMonthDays(searchDate);

		if (
			searchDate.getDay() === weekday &&
			getProgressionPos(searchDate) === counter &&
			searchDate.getTime() >= today.getTime()
		) {
			break;
		}

		if (maxDays === searchDate.getDate()) {
			searchDate.setDate(1);
			searchDate.setMonth(searchDate.getMonth() + recurrenceValue);
		} else {
			searchDate.setDate(searchDate.getDate() + 1);
		}

		iterations++;
	}

	return searchDate;
};

const calculateNextOrderMonthlyDate = (startDate: string, monthlyDay: number, recurrenceValue: number): Date => {
	const start = new Date(startDate);
	const today = new Date();

	start.setHours(12, 0, 0, 0);
	today.setHours(12, 0, 0, 0);

	// Set searchDate to the startDate and set searchDate's day to MonthlyDay from parameters or last of month if it's greater than monthlyDay
	let maxDays = getDateMonthDays(start);
	const searchDate = new Date(new Date(start.getTime()).setDate(monthlyDay > maxDays ? maxDays : monthlyDay));

	// If searchDate is on or after startDate and today, return searchDate
	if (searchDate.getTime() >= start.getTime() && searchDate.getTime() >= today.getTime()) return searchDate;

	let iterationLimit = 1000; // Prevent infinite loops
	let iterations = 0;

	// While searchDate is before today or startDate, keep adding months by recurrenceValue
	while (
		(today.getTime() > searchDate.getTime() || start.getTime() > searchDate.getTime()) &&
		iterations < iterationLimit
	) {
		searchDate.setDate(1);
		searchDate.setMonth(searchDate.getMonth() + recurrenceValue);
		const maxDays = getDateMonthDays(searchDate);
		searchDate.setDate(monthlyDay > maxDays ? maxDays : monthlyDay);

		iterations++;
	}

	return searchDate;
};

const nextOrderByPattern = (schedule: ApiSchedule): Date | null => {
	const { recurrenceValue, recurrencePattern, weeklyDays, startDate } = schedule;

	if (recurrencePattern === 'daily') {
		return calculateNextOrderDaily(startDate, recurrenceValue);
	}

	if (recurrencePattern === 'weekly' && Boolean(parseWeeklyDays(weeklyDays).length)) {
		return calculateNextOrderWeekly(startDate, parseWeeklyDays(weeklyDays), recurrenceValue);
	}

	if (recurrencePattern === 'monthly') {
		return calculateNextOrderMonthly(schedule);
	}

	return null;
};

export const calculateNextOrderDate = (schedule: ApiSchedule): Date | null => {
	let nextOrderDate = null;

	if (schedule.recurrenceValue) {
		nextOrderDate = nextOrderByPattern(schedule);
	}

	if (nextOrderDate && schedule.endDate && nextOrderDate.getTime() > new Date(schedule.endDate).getTime())
		return null;

	return nextOrderDate;
};
